import { useEffect, useState } from 'react';
import { api, type RunJobResult } from '../api-client';
import { tenantStore } from '../lib/tenant-store';

export default function RunJob() {
  const tenantId = tenantStore.get() ?? '';
  const [registry, setRegistry] = useState<{
    cartSources: { id: string; name: string }[];
    connectors: { id: string; name: string }[];
    deliveries: { id: string; name: string }[];
  } | null>(null);
  const [cartSource, setCartSource] = useState('bcart-mock');
  const [connector, setConnector] = useState('yayoi-hanbai');
  const [delivery, setDelivery] = useState('email');
  const [emailTo, setEmailTo] = useState('');
  const [subject, setSubject] = useState('受注データ');
  const [result, setResult] = useState<RunJobResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.registryInfo().then(setRegistry).catch(() => {});
  }, []);

  if (!tenantId) return <p>先にテナントを設定してください</p>;

  async function handleRun() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.runJob({
        tenantId,
        cartSource,
        connector,
        delivery,
        deliverySettings: { to: emailTo.split(',').map((e) => e.trim()).filter(Boolean), subject },
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">ジョブ手動実行</h2>

      <div className="bg-white rounded shadow border p-6 max-w-2xl space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">カートソース</label>
          <select value={cartSource} onChange={(e) => setCartSource(e.target.value)} className="w-full px-3 py-2 border rounded">
            {registry?.cartSources.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">連携先（Connector）</label>
          <select value={connector} onChange={(e) => setConnector(e.target.value)} className="w-full px-3 py-2 border rounded">
            {registry?.connectors.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">配信手段</label>
          <select value={delivery} onChange={(e) => setDelivery(e.target.value)} className="w-full px-3 py-2 border rounded">
            {registry?.deliveries.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        {delivery === 'email' && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">宛先メール（カンマ区切り）</label>
              <input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="example@example.com"
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">件名</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full px-3 py-2 border rounded" />
            </div>
          </>
        )}

        <button
          disabled={running}
          onClick={handleRun}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {running ? '実行中…' : 'ジョブ実行'}
        </button>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        {result && (
          <div className="bg-slate-50 p-4 rounded text-sm">
            <p className="font-semibold mb-2">
              {result.success ? '✓ 成功' : '✗ 失敗'} (Job {result.jobId.slice(0, 8)})
            </p>
            <p>受注数: {result.orderCount}</p>
            <p>CSV行数: {result.rowCount}</p>
            <p>処理時間: {result.durationMs}ms</p>
            {result.deliveryRef && <p>配信ID: {result.deliveryRef}</p>}
            {result.error && <p className="text-red-600">エラー: {result.error}</p>}
            {result.success && (
              <a
                href={`${import.meta.env.VITE_API_BASE ?? '/api'}/jobs/${result.jobId}/output.csv?tenantId=${tenantId}`}
                className="text-blue-600 underline mt-2 inline-block"
                target="_blank"
                rel="noreferrer"
              >
                CSVダウンロード →
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
